import { useOutletContext, Link } from "react-router-dom";
import { Hotel, MapPin, Music, Phone } from "lucide-react";
import type { PortalOutletContext } from "@/pages/artist-portal/ArtistPortalDetailPage";

export function OverviewTab() {
  const { overview, festivalId } = useOutletContext<PortalOutletContext>();
  const nextSlot = overview.my_slots[0];

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Next set */}
      <div className="card p-4">
        <div className="flex items-center gap-2 text-ink-3">
          <Music className="h-4 w-4" /> Next set
        </div>
        {nextSlot ? (
          <>
            <div className="mt-2 text-2xl font-semibold text-ink">{nextSlot.stage_name}</div>
            <div className="text-sm text-ink-2">
              {nextSlot.day}
              {nextSlot.start_time ? ` · ${nextSlot.start_time}` : ""}
              {nextSlot.end_time ? ` → ${nextSlot.end_time}` : ""}
            </div>
          </>
        ) : (
          <div className="mt-2 text-sm text-ink-3">
            No set scheduled for you. The Lineup tab shows the full festival.
          </div>
        )}
      </div>

      {/* Hotel */}
      <div className="card p-4">
        <div className="flex items-center gap-2 text-ink-3">
          <Hotel className="h-4 w-4" /> Hotel
        </div>
        {overview.my_hotel ? (
          <>
            <div className="mt-2 text-xl font-semibold text-ink">
              {overview.my_hotel.hotel_name}
            </div>
            <div className="text-sm text-ink-2">
              {overview.my_hotel.check_in && <>Check-in {overview.my_hotel.check_in}</>}
              {overview.my_hotel.check_out && <> · Check-out {overview.my_hotel.check_out}</>}
              {overview.my_hotel.room_type && <> · {overview.my_hotel.room_type}</>}
            </div>
          </>
        ) : (
          <div className="mt-2 text-sm text-ink-3">No hotel assigned.</div>
        )}
      </div>

      {/* Venue */}
      {overview.festival.venue_address && (
        <div className="card p-4 md:col-span-2">
          <div className="flex items-center gap-2 text-ink-3">
            <MapPin className="h-4 w-4" /> Venue
          </div>
          <div className="mt-2 text-sm text-ink-2">{overview.festival.venue_address}</div>
        </div>
      )}

      {/* Top emergency contacts */}
      <div className="card p-4 md:col-span-2">
        <div className="mb-3 flex items-center gap-2 text-ink-3">
          <Phone className="h-4 w-4" /> Top emergency contacts
        </div>
        {overview.emergency_contacts.length === 0 ? (
          <div className="text-sm text-ink-3">No emergency contacts yet.</div>
        ) : (
          <>
            <ul className="space-y-2">
              {overview.emergency_contacts.slice(0, 3).map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between rounded border border-line-2 p-2"
                >
                  <div>
                    <div className="font-semibold text-ink">{c.name}</div>
                    <div className="text-xs text-ink-3">{c.role}</div>
                  </div>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="text-sm text-accent">
                      {c.phone}
                    </a>
                  )}
                </li>
              ))}
            </ul>
            {overview.emergency_contacts.length > 3 && (
              <Link
                to={`/my-schedule/${festivalId}/emergency`}
                className="mt-3 inline-block text-sm text-accent hover:underline"
              >
                View all ({overview.emergency_contacts.length})
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
